import {
  RouteProp,
  useFocusEffect,
  useNavigation,
  useRoute
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { Fragment, useCallback } from 'react';
import styled from 'styled-components/native';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import AlertBox from '../../components/AlertBox';
import Button from '../../components/Button';
import HeaderTabView from '../../components/HeaderTabView';
import Spacer from '../../components/Spacer';
import { P } from '../../components/Typography';
import Colors from '../../constants/Colors';
import { useGetScreen } from '../../hooks/screen/useGetScreen';
import { ScreenSection } from '../../models/Screen';
import { DiscoverParamList } from '../../navigation/DiscoverNavigator';
import DynamicScreenComponentView from '../screen/DynamicScreenComponentView';
import { useUserContext } from '../../context/UserContext';
import { useHasActiveStudy } from '../../hooks/study/useHasActiveStudy';
import { log } from '../../tools/LogHelper';
import accessibilityTags from '../../../accessibilityTags';

const StyledScrollView = styled.ScrollView`
  background-color: ${Colors.background};
  padding: 12px;
`;

const StyledCreateView = styled.View`
  padding: 12px;
`;

const StyledCardView = styled.View`
  background: #e6e3fe;
  border-radius: 10px;
  padding: 16px 0px 0px 16px;
  flex-direction: row;
  align-items: flex-end;
  overflow: hidden;
`;

const StyledEmojiImage = styled.Image<{
  mirror?: boolean;
  size: number;
  marginBottom?: number;
}>`
  height: ${p => p.size}px;
  width: 100%;
  ${p => (p.mirror ? 'transform: scale(-1, 1);' : '')};
  margin-bottom: ${p => (p.marginBottom ? p.marginBottom : 0)}px;
`;

const StyledEmojiView = styled.View`
  width: 98px;
  justify-content: flex-end;
  left: 4px;
  overflow: visible;
`;

const StyledChatBubbleView = styled.View`
  flex-direction: row;
  flex: 1;
  height: 100%;
  padding-bottom: 40px;
  align-items: flex-end;
`;

const StyledChatBubble = styled.View`
  border-radius: 10px;
  width: 100%;
  height: 100%;
  padding: 24px 16px 16px 16px;
  background-color: white;
`;

const MessageArrow = styled.View`
  background-color: transparent;
  border-style: solid;
  border-top-width: 12px;
  border-bottom-width: 12px;
  border-top-color: transparent;
  border-bottom-color: transparent;
  border-left-width: 9px;
  border-left-color: white;
  height: 15px;
  bottom: 12px;
`;

const StyledOngoingStudyView = styled.View`
  height: 100%;
  width: 100%;
  padding: 12px;
  align-items: center;
  background-color: ${Colors.background};
`;

const StyledText = styled.Text`
  font-family: 'WorkSans-Regular';
  font-size: 20px;
  line-height: 28px;
  color: ${Colors.darkText};
  text-align: center;
`;

const StyledLightBulbImage = styled.Image`
  height: 48px;
  width: 48px;
  align-self: center;
`;

const StyledScientistImage = styled.Image`
  height: 90px;
  width: 90px;
  align-self: center;
  transform: scaleX(-1);
`;

const StyledNavigateToStudyTasksButton = styled(Button)`
  width: 100%;
  max-width: 480px;
`;

const DiscoverScreen: React.FC = () => {
  const route = useRoute<RouteProp<DiscoverParamList, 'Discover'>>();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { screen, loadingScreen, loadingScreenError, getScreen } = useGetScreen(
    ScreenSection.Join
  );
  const { loadingUser, loadingStudyParticipants } = useUserContext();
  useFocusEffect(
    useCallback(() => {
      getScreen();
    }, [getScreen])
  );
  const hasActiveStudy = useHasActiveStudy();
  return (
    <Fragment>
      {hasActiveStudy ? (
        <StyledOngoingStudyView>
          <Spacer />
          <StyledText>
            Hello Scientist!{`\n`}
            You’re currently{`\n`}
            enrolled in a study.
          </StyledText>
          <Spacer size={3} />
          <StyledText>
            Come explore other{`\n`}
            studies to join when{`\n`}
            you’re finished!
          </StyledText>
          <Spacer size={3} />
          <StyledLightBulbImage
            resizeMode='contain'
            source={require('../../assets/icon-light-bulb.png')}
          />
          <Spacer />
          <StyledScientistImage
            resizeMode='contain'
            source={require('../../assets/icon-scientist.png')}
          />
          <Spacer size={8} />
          <StyledNavigateToStudyTasksButton
            fullWidth
            color='secondary'
            onPress={() => {
              navigation.navigate('ToDoNav');
            }}
          >
            See My Study Tasks
          </StyledNavigateToStudyTasksButton>
        </StyledOngoingStudyView>
      ) : (
        <StyledScrollView>
          {/* <HeaderTabView
            value={route.params?.tab}
            tabs={['Join', 'Create']}
            onTabChange={(_, tab) => {
              log('Selected Tab:', tab);
              if (tab === 'Create') {
                navigation.setParams({ tab: 'Create' });
              } else {
                navigation.setParams({ tab: 'Join' });
              }
            }}
          />
          <Spacer size={2} /> */}
          {loadingScreenError && (
            <>
              <AlertBox severity='error'>{loadingScreenError.message}</AlertBox>
              <Spacer size={2} />
            </>
          )}
          {(() => {
            // switch (route.params?.tab) {
            //   case 'Create':
            //     return (
            //       <StyledCreateView>
            //         <StyledCardView>
            //           <StyledChatBubbleView>
            //             <StyledChatBubble>
            //               <P color='dark'>
            //                 Already have something you’re excited to try?
            //                 {'\n\n'}
            //                 Create a custom study in under a minute to learn
            //                 about what works for you.
            //               </P>
            //             </StyledChatBubble>
            //             <MessageArrow />
            //           </StyledChatBubbleView>
            //           <StyledEmojiView>
            //             <StyledEmojiImage
            //               size={40}
            //               resizeMode='contain'
            //               marginBottom={8}
            //               source={require('../../assets/icon-light-bulb.png')}
            //             />
            //             <StyledEmojiImage
            //               mirror
            //               resizeMode='contain'
            //               size={98}
            //               source={require('../../assets/icon-scientist.png')}
            //             />
            //           </StyledEmojiView>
            //         </StyledCardView>
            //         <Spacer size={4} />
            //         <Button
            //           accessibilityLabel={
            //             accessibilityTags.startCreatingButtonID
            //           }
            //           testID={accessibilityTags.startCreatingButtonID}
            //           color='secondary'
            //           onPress={() => {
            //             navigation.navigate('StudiesNav', {
            //               screen: 'FormTemplate',
            //               params: {
            //                 formTemplateName: 'Start Study'
            //               }
            //             });
            //           }}
            //         >
            //           Start Creating Here!
            //         </Button>
            //       </StyledCreateView>
            //     );
            //   default:
            return (
              <DynamicScreenComponentView
                components={screen?.components || []}
              />
            );
            // }
          })()}
        </StyledScrollView>
      )}
      {(loadingScreen || loadingUser || loadingStudyParticipants) &&
        !screen && <ActivityIndicatorScreen modal />}
    </Fragment>
  );
};

export default DiscoverScreen;
